import { Card, CardBody, Icon } from '@statsbomb/kitbag-components';
import { ResponsiveRadar, RadarData as RadarDataVis } from '@statsbomb/kitbag-datavis';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { defaultSeasonsAtom } from '@/atoms/filters/highLevel/seasons';
import { metricsAtom } from '@/atoms/metric';
import {
  RADAR_VIEW,
  radarViewAtom,
  renderedComparisonRadarDataAtom,
  renderedRadarDataAtom,
  unwrappedRadarDataFormatAtom,
  unwrappedRadarTableComparisonPercentilesAtom,
  unwrappedRadarTableMetricPercentilesAtom,
  unwrappedSelectedRadarTemplateAtom,
} from '@/atoms/radar';
import { selectedVisualisationAtom, showArrowsAtom } from '@/atoms/visualisation';
import { METRIC_KEY } from '@/consts/searchKeys';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { useDeepEffect } from '@/hooks/useDeepEffect';
import { useGetEntity } from '@/hooks/useGetEntity';
import { useHasEmptyRadar } from '@/hooks/useHasEmptyRadar';
import { useIsComparisonActive } from '@/hooks/useIsComparisonActive';
import { useResetSelectionLayer } from '@/hooks/useResetSelectionLayer';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { SBID } from '@/types/generic';
import { PHYSICAL_SOURCE } from '@/types/metric';
import { ComparisonRadarDataAtom, LoadableRadarDataAtom, SelectedGamesAtom, RadarDataFormat } from '@/types/radar';
import { StatusTranslationKey, StatusType } from '@/types/status';
import { toPercentileRadarData } from '@/utils/radar';
import { getMetricTranslations } from '@/utils/translations';
import { CardHeightWrapper } from '../card/CardHeightWrapper';
import { RadarDistributionList } from './RadarDistributionList';

export const RadarChartBase = ({
  id,
  isLoading,
  isInteractive = true,
}: {
  id: SBID;
  isLoading?: boolean;
  isInteractive?: boolean;
}) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation(['metrics', 'status', 'radar']);
  const navigate = useNavigate();
  const { entity } = useGetEntity();
  const [searchParams] = useSearchParams();
  const resetSelectionLayer = useResetSelectionLayer();
  const physicalMetricsEnabled = usePhysicalMetricsFeature();
  const createQueryString = useUpdateQueryString(searchParams);
  const isRadarPage = !!useMatch('/:locale/:entity/:id/radar/*');
  const isComparisonActive = useIsComparisonActive();
  const view = useAtomValue(radarViewAtom);
  const isDistributionView = isInteractive && view === RADAR_VIEW.DISTRIBUTION;

  const renderedRadarData = useAtomValue(renderedRadarDataAtom);
  const renderedComparisonData = useAtomValue(renderedComparisonRadarDataAtom);
  const metricPercentiles = useAtomValue(unwrappedRadarTableMetricPercentilesAtom);
  const comparisonMetricPercentiles = useAtomValue(unwrappedRadarTableComparisonPercentilesAtom);
  const radarDataFormat = useAtomValue(unwrappedRadarDataFormatAtom);
  const isPercentileMode = radarDataFormat === RadarDataFormat.Percentiles;
  const setSelectedVis = useSetAtom(selectedVisualisationAtom);
  const setShowArrows = useSetAtom(showArrowsAtom);
  const metricKeyParam = searchParams.get(METRIC_KEY) || '';
  const isComparison = !!renderedComparisonData?.length && isComparisonActive && isRadarPage;

  // guard against metric key value from the url not existing in the radar data
  const selectedMetricKey = renderedRadarData.find(d => d.key === metricKeyParam) ? metricKeyParam : undefined;

  const baseRadarData = isPercentileMode
    ? toPercentileRadarData(renderedRadarData, metricPercentiles)
    : renderedRadarData;

  const getComparisonValue = (key: string) => {
    if (!isComparison) return undefined;
    if (isPercentileMode) return comparisonMetricPercentiles[key];
    return renderedComparisonData?.find(c => c.key === key)?.value;
  };

  const translatedRenderedRadarData = baseRadarData.map(data => {
    const { name, longName, description } = getMetricTranslations(t, data.key);
    const isPhysical = physicalMetricsEnabled && data.source === PHYSICAL_SOURCE;

    return {
      ...data,
      label: name,
      // Untranslated metrics return '', which would render an empty header instead of falling back to the label.
      tooltipTitle: longName || undefined,
      tooltip: description,
      comparisonValue: getComparisonValue(data.key),
      // Only mark a spoke non-interactive on an interactive chart. On a static export
      // (isInteractive=false) nonInteractive removes the slice entirely, leaving a gap in the
      // radar shape — so we skip it and let the full shape render.
      nonInteractive: isInteractive && isPhysical,
      icon: isPhysical ? <Icon variant="Tracking" size="small" /> : undefined,
      iconTooltip: isPhysical ? t('trackingIconTooltip', { ns: 'radar' }) : undefined,
    };
  });

  const handleClick = (metric: RadarDataVis) => {
    const dataForSlice = translatedRenderedRadarData.find(d => d.key === metric.key);
    if (!dataForSlice) return;

    setSelectedVis(null);
    setShowArrows(false);
    resetSelectionLayer();

    const isSameKey = metric.key === metricKeyParam;
    const paramsToAdd = isSameKey ? [] : [{ key: METRIC_KEY, value: dataForSlice.key }];
    const paramsToDelete = isSameKey ? [METRIC_KEY] : [];
    const navigationPath = isSameKey ? './' : 'preview';

    navigate(`${navigationPath}?${createQueryString(paramsToAdd, paramsToDelete)}`);
  };

  const hasEmptyRadar = useHasEmptyRadar();
  const hasNoData = !isLoading && hasEmptyRadar;

  const getStatusProps = () => {
    if (isLoading) {
      return {
        statusDescription: t('general.loading.description', { ns: 'status' }),
        statusTitle: t('general.loading.title', { ns: 'status' }),
        statusType: 'loading',
      };
    }
    if (hasNoData) {
      return {
        statusDescription: t(`general.${StatusTranslationKey.NO_DATA}.description`, { ns: 'status' }),
        statusTitle: t(`general.${StatusTranslationKey.NO_DATA}.title`, { ns: 'status' }),
        statusType: StatusType.noData,
      };
    }
    return null;
  };

  const statusProps = getStatusProps();

  const distributionData = useMemo(() => {
    if (!isDistributionView) return [];
    const comparisonByKey = isComparison
      ? new Map(renderedComparisonData.map(comparison => [comparison.key, comparison]))
      : null;

    return renderedRadarData.map(metric => {
      const comparison = comparisonByKey?.get(metric.key);
      return {
        ...metric,
        label: getMetricTranslations(t, metric.key).name,
        ...(comparison && { comparisonValue: comparison.value, comparisonHasValue: comparison.hasValue }),
      };
    });
  }, [isDistributionView, isComparison, renderedComparisonData, renderedRadarData, t]);

  return (
    <Card>
      <CardHeightWrapper>
        <CardBody {...statusProps}>
          {isDistributionView ? (
            <Suspense fallback={<div>{t('distributionView.loading', { ns: 'radar' })}</div>}>
              <RadarDistributionList
                data={distributionData}
                selectedKey={selectedMetricKey}
                onRowClick={key => {
                  const metric = translatedRenderedRadarData.find(d => d.key === key);
                  if (metric && !metric.nonInteractive) handleClick(metric);
                }}
              />
            </Suspense>
          ) : (
            <ResponsiveRadar
              id={`${entity}-${id}`}
              data={translatedRenderedRadarData}
              onSliceClick={isInteractive ? handleClick : undefined}
              locale={locale}
              selectedKey={selectedMetricKey}
            />
          )}
        </CardBody>
      </CardHeightWrapper>
    </Card>
  );
};

// We want to know when we're loading new data for the radar so that we can render a loading message.
// But we can't use suspense in this case as that will cause us to lose the smooth animation from one
// radar state to the next (I don't really understand this to be honest, but allowing it to fallback to
// the suspense causes a full radar to be rendered)
const RadarChartWithData = ({
  id,
  loadableRadarDataAtom,
  comparisonRadarDataAtom,
  isInteractive,
  isLoading,
}: {
  id: SBID;
  loadableRadarDataAtom: LoadableRadarDataAtom;
  comparisonRadarDataAtom: ComparisonRadarDataAtom;
  isInteractive?: boolean;
  isLoading?: boolean;
}) => {
  const newRadarDataLoadable = useAtomValue(loadableRadarDataAtom);
  const { data: comparisonData, isLoading: comparisonIsLoading } = useAtomValue(comparisonRadarDataAtom);
  const isComparisonActive = useIsComparisonActive();

  const isComparing = isComparisonActive && comparisonIsLoading;
  const newRadarData = newRadarDataLoadable.state === 'hasData' && newRadarDataLoadable.data;
  const setRenderedRadarData = useSetAtom(renderedRadarDataAtom);
  const setRenderedComparisonData = useSetAtom(renderedComparisonRadarDataAtom);
  const selectedTemplate = useAtomValue(unwrappedSelectedRadarTemplateAtom);
  const setMetrics = useSetAtom(metricsAtom);

  useDeepEffect(() => {
    if (selectedTemplate) {
      setMetrics(selectedTemplate.definition.metrics.map(metric => metric.key));
    }
  }, [selectedTemplate]);

  useDeepEffect(() => {
    if (newRadarData && !comparisonIsLoading) {
      setRenderedRadarData(newRadarData);
      setRenderedComparisonData(isComparisonActive ? comparisonData : []);
    }
  }, [newRadarData, comparisonData, comparisonIsLoading, isComparisonActive]);

  return (
    <RadarChartBase
      id={id}
      isLoading={isLoading || newRadarDataLoadable.state === 'loading' || isComparing}
      isInteractive={isInteractive}
    />
  );
};

// We want to prevent the aggregates query from being called until there are selected
// games and default seasons are populated. This component ensures that by rendering a
// placeholder component until those things are loaded
const RadarChartWithFallback = ({
  id,
  loadableSelectedGamesAtom,
  loadableRadarDataAtom,
  comparisonRadarDataAtom,
  isInteractive,
}: {
  id: SBID;
  loadableSelectedGamesAtom: SelectedGamesAtom;
  loadableRadarDataAtom: LoadableRadarDataAtom;
  comparisonRadarDataAtom: ComparisonRadarDataAtom;
  isInteractive?: boolean;
}) => {
  const defaultSeasons = useAtomValue(defaultSeasonsAtom);
  const loadableSelectedGames = useAtomValue(loadableSelectedGamesAtom);

  return (
    <RadarChartWithData
      id={id}
      loadableRadarDataAtom={loadableRadarDataAtom}
      comparisonRadarDataAtom={comparisonRadarDataAtom}
      isInteractive={isInteractive}
      isLoading={!defaultSeasons || loadableSelectedGames.state === 'loading'}
    />
  );
};

export const RadarChart = ({
  id,
  loadableSelectedGamesAtom,
  loadableRadarDataAtom,
  comparisonRadarDataAtom,
  isWidget = false,
  isInteractive,
}: {
  id: SBID;
  loadableSelectedGamesAtom: SelectedGamesAtom;
  loadableRadarDataAtom: LoadableRadarDataAtom;
  comparisonRadarDataAtom: ComparisonRadarDataAtom;
  isWidget?: boolean;
  isInteractive?: boolean;
}) =>
  // we don't care about waiting on the default seasons if in a widget
  isWidget ? (
    <RadarChartWithData
      id={id}
      loadableRadarDataAtom={loadableRadarDataAtom}
      comparisonRadarDataAtom={comparisonRadarDataAtom}
      isInteractive={false}
    />
  ) : (
    <RadarChartWithFallback
      id={id}
      loadableSelectedGamesAtom={loadableSelectedGamesAtom}
      loadableRadarDataAtom={loadableRadarDataAtom}
      comparisonRadarDataAtom={comparisonRadarDataAtom}
      isInteractive={isInteractive}
    />
  );
